// imports
import { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

// local imports
import ReusableMenu from '../ReusableMenu';
import { setTemporalResolution } from '../../../../store/waterDataSlice';
import { getAvailableTemporalResolutions } from '../../../../constants/TemporalResolution';
import { getUrlParam } from '../../../../utils/getUrlParam';

const TemporalResolutionDisplay = getAvailableTemporalResolutions();

const temporalResolutionOptions = Object.entries(TemporalResolutionDisplay).map(
  ([key, value]) => ({
    key,
    value,
  })
);

interface TemporalResolutionMenuProps {
  setIsMonthly: (isMonthly: boolean) => void;
  setSelectedTemporalResolution?: (temporalResolution: string) => void;
}

// setIsMonthly determines if the month menu is displayed in DateMenu
export default function TemporalResolutionMenu({
  setIsMonthly,
  setSelectedTemporalResolution,
}: TemporalResolutionMenuProps) {
  const dispatch = useDispatch();

  // Initialize from URL parameter if it exists
  const [selectedIndex, setSelectedIndex] = useState(() => {
    const temporalFromUrl = getUrlParam('temporalResolution');
    if (temporalFromUrl) {
      const foundIndex = temporalResolutionOptions.findIndex(
        (opt) => opt.key === temporalFromUrl
      );
      return foundIndex !== -1 ? foundIndex : 0;
    }
    return 0;
  });

  const handleSelect = useCallback(
    (index: number) => {
      setSelectedIndex(index);
      const selectedKey = temporalResolutionOptions[index].key;
      dispatch(setTemporalResolution(selectedKey));
      setIsMonthly(selectedKey === 'monthly');
      setSelectedTemporalResolution?.(selectedKey);
    },
    [dispatch, setIsMonthly, setSelectedTemporalResolution]
  );

  // Initialize on mount with current selection
  useEffect(() => {
    const selectedKey = temporalResolutionOptions[selectedIndex].key;
    dispatch(setTemporalResolution(selectedKey));
    setIsMonthly(selectedKey === 'monthly');
    setSelectedTemporalResolution?.(selectedKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ReusableMenu
      title="Temporal Resolution"
      options={temporalResolutionOptions}
      selectedIndex={selectedIndex}
      onSelect={handleSelect}
    />
  );
}
